/**
 * 管理端期限卡 API（period-pass-entitlement，ADM-24 期限卡段）。
 *
 * 对齐后端 entitlement 模块 REST 契约（O-14 新增）：
 * - PATCH /period-passes/:id/start-date      -> 调整开始日期（ADM-24:PERIOD_ADJUST 动作）
 * - GET   /students/:studentId/period-passes -> 学生期限卡列表（ADM-24 卡片区数据源）
 * - GET   /period-passes/:id                 -> 期限卡详情（业务编号 PERIOD_PASS 下钻摘要）
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 * 展示状态（displayStatus）与剩余天数（remainingDays）为服务端派生字段，
 * 前端不重复计算区间（design D4：读侧纯函数三端同口径）。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 期限卡落库状态（已到期为展示派生态，不落库）。 */
export type PeriodPassStatus = 'PENDING_ACTIVATION' | 'ACTIVE';

/** 期限卡展示状态（含派生态已到期）。 */
export type PeriodPassDisplayStatus = PeriodPassStatus | 'EXPIRED';

/** 期限卡摘要视图（学生列表项与单卡详情共用）。 */
export interface PeriodPassView {
  periodPassId: string;
  sourceOrderNo: string | null;
  /** 所属学生 ID（两端均返回）。 */
  studentId?: string;
  /** 学生姓名（列表/详情读侧均补齐）。 */
  studentName: string;
  skuId: string;
  skuName: string;
  /** 落库状态（待激活/生效中） */
  status: PeriodPassStatus;
  /** 展示状态（服务端派生，含已到期） */
  displayStatus: PeriodPassDisplayStatus;
  /** 生效开始日（YYYY-MM-DD，待激活 null） */
  startDate: string | null;
  /** 到期日（YYYY-MM-DD，start + periodDays − 1 含当天，待激活 null） */
  endDate: string | null;
  /** 期限天数（下单快照） */
  periodDays: number;
  /** 剩余天数（仅生效中有意义，其余为 0） */
  remainingDays: number;
  /** 来源订单 ID */
  sourceOrderId: string;
  /** 同账户排队位次（按购买先后，1 起） */
  queuePosition: number;
  /** 乐观锁版本（调整开始日期请求回传） */
  version: number;
}

/** 调整开始日期请求体（原因必填 ≤100 字；version 乐观锁）。 */
export interface AdjustPeriodStartDatePayload {
  startDate: string;
  reason: string;
  version: number;
}

/** 调整开始日期响应（新区间即时回显）。 */
export interface AdjustPeriodStartDateResult {
  id: string;
  status: PeriodPassStatus;
  startDate: string;
  endDate: string;
  displayStatus: PeriodPassDisplayStatus;
  remainingDays: number;
}

/** 调整期限卡开始日期（PATCH；409=已到期/乐观锁冲突，400=原因缺失/超长）。 */
async function adjustStartDate(
  id: string,
  payload: AdjustPeriodStartDatePayload,
): Promise<AdjustPeriodStartDateResult> {
  return http.patch<AdjustPeriodStartDateResult>(
    ApiPaths.periodPasses.adjustStartDate(id),
    payload,
  );
}

/** 学生期限卡列表（按购买先后排序；无卡返回空数组）。服务端 data 为裸数组（非分页信封）。 */
async function listStudentPeriodPasses(studentId: string): Promise<PeriodPassView[]> {
  return http.get<PeriodPassView[]>(ApiPaths.periodPasses.byStudent(studentId));
}

/** 期限卡详情（业务编号 PERIOD_PASS 下钻；404 由调用方回退说明抽屉）。 */
async function getPeriodPass(id: string): Promise<PeriodPassView> {
  return http.get<PeriodPassView>(ApiPaths.periodPasses.detail(id));
}

/** 管理端期限卡 API 唯一对外入口。 */
export const entitlementApi = {
  adjustStartDate,
  listStudentPeriodPasses,
  getPeriodPass,
};
